import { ChefHat } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";


const LAST_UPDATED_NL = "februari 2026";
const OPERATOR = "Innovations EOOD";
const OPERATOR_ADDRESS_LINES = [
  "Innovations EOOD",
  "Ulitsa Stanislav Dospevski 7",
  "Plovdiv 4000",
  "Bulgaria",
  "VAT: BG204631132",
];
const CONTACT_EMAIL = "hello@smartly.cooking";


const ContactBlock = ({ testid }) => (
  <div className="rounded-2xl border border-stone-200 bg-white/80 p-4 not-prose" data-testid={testid}>
    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Operator</p>
    <address className="mt-2 not-italic text-sm leading-relaxed text-stone-800" data-testid={`${testid}-address`}>
      {OPERATOR_ADDRESS_LINES.map((line) => (
        <span key={line} className="block">{line}</span>
      ))}
    </address>
    <p className="mt-3 text-xs font-semibold uppercase tracking-[0.24em] text-stone-500">Contact</p>
    <p className="mt-2 text-sm text-stone-800">
      <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium text-emerald-700 hover:text-emerald-800" data-testid={`${testid}-email`}>
        {CONTACT_EMAIL}
      </a>
    </p>
  </div>
);


/**
 * Shared shell for all public legal/trust pages. Matches the landing
 * visual style (same background, nav, footer) so guests don't feel
 * transported to a different product.
 */
const LegalShell = ({ title, kicker, children, testid }) => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(255,209,102,0.28),_transparent_38%),linear-gradient(180deg,_#fbf7ef_0%,_#f4efe3_50%,_#edf3ea_100%)] text-stone-900" data-testid={testid}>
      <header className="sticky top-0 z-30 border-b border-stone-200/70 bg-[#fbf7ef]/85 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-stone-900" data-testid="legal-nav-logo">
            <span className="grid h-9 w-9 place-items-center rounded-2xl bg-emerald-700 text-white">
              <ChefHat className="h-5 w-5" />
            </span>
            <span className="text-base font-semibold tracking-tight">Smartly</span>
          </Link>
          <Button onClick={() => navigate("/")} variant="outline" className="h-10 rounded-full border-stone-300 bg-white px-4 text-stone-800 hover:bg-stone-50" data-testid="legal-back-button">
            Terug
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-12 sm:py-16">
        <p className="text-xs font-semibold uppercase tracking-[0.32em] text-emerald-700" data-testid="legal-kicker">{kicker}</p>
        <h1 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl" data-testid="legal-title">{title}</h1>
        <p className="mt-2 text-xs text-stone-500" data-testid="legal-last-updated">Laatst bijgewerkt: {LAST_UPDATED_NL}</p>

        <div className="prose-smartly mt-8 space-y-7 text-[15px] leading-relaxed text-stone-800 sm:text-base">
          {children}
        </div>

        <p className="mt-12 text-xs text-stone-500" data-testid="legal-mvp-note">
          Smartly is een kleine SaaS in opbouw. Dit beleid kan worden bijgewerkt naarmate het product groeit.
        </p>
      </main>

      <footer className="border-t border-stone-200/80 bg-[#f4efe3]/60">
        <div className="mx-auto flex max-w-3xl flex-col gap-3 px-5 py-6 text-xs text-stone-600 sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} Smartly</p>
          <nav className="flex flex-wrap gap-4">
            <Link to="/privacy" className="hover:text-stone-900">Privacy</Link>
            <Link to="/terms" className="hover:text-stone-900">Voorwaarden</Link>
            <Link to="/refund-policy" className="hover:text-stone-900">Opzeggen &amp; restitutie</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
};


const Section = ({ heading, children, testid }) => (
  <section data-testid={testid}>
    <h2 className="text-lg font-semibold tracking-tight text-stone-900 sm:text-xl">{heading}</h2>
    <div className="mt-3 space-y-3">{children}</div>
  </section>
);


// ----------------------------------------------------------------- Privacy

export const PrivacyPage = () => (
  <LegalShell title="Privacyverklaring" kicker="Privacy" testid="privacy-page">
    <Section heading="1. Wat is Smartly?" testid="privacy-section-what">
      <p>
        Smartly is een web-app waarmee je je maaltijden, voorraad en boodschappen plant. Deze
        privacyverklaring beschrijft welke persoonsgegevens we verwerken wanneer je Smartly gebruikt
        en waarom we dat doen.
      </p>
    </Section>

    <Section heading="2. Welke gegevens we verwerken" testid="privacy-section-data">
      <ul className="list-disc space-y-1 pl-5">
        <li>Accountgegevens (naam, e-mailadres, gehasht wachtwoord, taalvoorkeur, huishoudgrootte).</li>
        <li>Maaltijdplanningsgegevens (weekplanner, voorkeuren, lock/swap-acties).</li>
        <li>Pantry- en boodschappengegevens (items die je toevoegt, hoeveelheden, houdbaarheid).</li>
        <li>Recept- en macrovoorkeuren die je instelt.</li>
        <li>Optionele agenda-gegevens uitsluitend als jij Google Calendar koppelt.</li>
        <li>Abonnementsstatus zodra Premium beschikbaar is (plan, status, betaalperiode). Betaalkaartgegevens worden door Smartly nooit ontvangen of opgeslagen.</li>
      </ul>
    </Section>

    <Section heading="3. Waarom we deze gegevens verwerken" testid="privacy-section-why">
      <ul className="list-disc space-y-1 pl-5">
        <li>Toegang tot je account en sessiebeheer.</li>
        <li>Het laten werken van planner, pantry en boodschappenlijst.</li>
        <li>Klantondersteuning bij vragen.</li>
        <li>Beheer van een Premium-abonnement zodra die functie actief is.</li>
      </ul>
    </Section>

    <Section heading="4. Externe verwerkers" testid="privacy-section-processors">
      <p>We schakelen een beperkt aantal externe diensten in. Met hen worden alleen gegevens gedeeld die strikt nodig zijn:</p>
      <ul className="list-disc space-y-1 pl-5">
        <li>Een hosting-provider voor het draaien van de app.</li>
        <li>Een database-/opslagprovider voor het bewaren van je gegevens.</li>
        <li>Stripe voor het verwerken van betalingen zodra Premium beschikbaar is.</li>
        <li>Een e-mailprovider (Resend) voor transactionele e-mails zoals wachtwoordherstel.</li>
        <li>Google, uitsluitend wanneer jij zelf Google Calendar koppelt, en alleen met read-only scope.</li>
      </ul>
    </Section>

    <Section heading="5. Jouw rechten" testid="privacy-section-rights">
      <ul className="list-disc space-y-1 pl-5">
        <li>Inzage in de gegevens die we van je verwerken.</li>
        <li>Correctie van onjuiste gegevens.</li>
        <li>Verwijderen van je account en bijbehorende gegevens (via /profile in de app).</li>
        <li>Contact opnemen via het onderstaande adres.</li>
      </ul>
    </Section>

    <Section heading="6. Bewaartermijn" testid="privacy-section-retention">
      <p>
        We bewaren je gegevens zolang je een actief account hebt. Wanneer je je account verwijdert,
        verwijderen of anonimiseren we deze gegevens waar dit technisch en wettelijk mogelijk is.
        Sommige administratieve gegevens (zoals betaal- en factuurgegevens) kunnen op grond van
        wettelijke bewaarplichten langer worden bewaard.
      </p>
    </Section>

    <Section heading="7. Beveiliging" testid="privacy-section-security">
      <p>
        We treffen redelijke technische en organisatorische maatregelen om je gegevens te beschermen,
        zoals versleuteling van gevoelige tokens en sterk gehashte wachtwoorden. Geen enkele
        online-dienst kan absolute veiligheid garanderen.
      </p>
    </Section>

    <Section heading="8. Contact" testid="privacy-section-contact">
      <p>Vragen over deze privacyverklaring? Neem contact op met {OPERATOR} via {CONTACT_EMAIL}.</p>
      <ContactBlock testid="privacy-contact-block" />
    </Section>
  </LegalShell>
);


// ------------------------------------------------------------------- Terms

export const TermsPage = () => (
  <LegalShell title="Algemene voorwaarden" kicker="Voorwaarden" testid="terms-page">
    <Section heading="1. Wat Smartly biedt" testid="terms-section-service">
      <p>
        Smartly is een web-app die je helpt bij maaltijdplanning, voorraadbeheer en het samenstellen
        van boodschappenlijsten. Deze voorwaarden zijn van toepassing op het gebruik van Smartly.
      </p>
    </Section>

    <Section heading="2. Jouw account" testid="terms-section-account">
      <p>
        Je bent zelf verantwoordelijk voor de juistheid van je accountgegevens en voor het
        geheimhouden van je wachtwoord. Als je vermoedt dat iemand anders toegang heeft tot je
        account, wijzig dan direct je wachtwoord en neem contact op.
      </p>
    </Section>

    <Section heading="3. Free en Premium" testid="terms-section-tiers">
      <p>
        Smartly kent twee abonnementsvormen: Free en Premium. Free is gratis te gebruiken binnen de
        gepubliceerde limieten. Premium ontgrendelt onder andere de volledige weekplanning, een
        onbeperkte pantry en slimmere boodschappenplanning.
      </p>
    </Section>

    <Section heading="4. Premium-abonnement en betaling" testid="terms-section-billing">
      <ul className="list-disc space-y-1 pl-5">
        <li>Premium is beschikbaar als maandelijks of jaarlijks abonnement.</li>
        <li>De huidige prijzen zijn € 7,99 per maand of € 59 per jaar (inclusief btw waar van toepassing).</li>
        <li>Betalingen worden verwerkt door Stripe. Smartly ontvangt of bewaart geen kaartgegevens.</li>
        <li>Je kunt je abonnement op elk moment opzeggen via je profiel; toegang blijft tot het einde van de betaalde periode beschikbaar.</li>
      </ul>
    </Section>

    <Section heading="5. Acceptabel gebruik" testid="terms-section-acceptable-use">
      <p>
        Gebruik Smartly niet voor onwettige doeleinden, geautomatiseerde scraping, het omzeilen van
        beveiligingsmaatregelen of het hinderen van andere gebruikers. We kunnen accounts beperken of
        beëindigen wanneer deze regels worden overtreden.
      </p>
    </Section>

    <Section heading="6. Geen medisch of voedingsadvies" testid="terms-section-no-medical">
      <p>
        Smartly is een planningstool, geen medische of voedingsdienst. Suggesties, calorie- en
        koolhydraatberekeningen zijn benaderingen op basis van door jou ingevoerde gegevens. Je blijft
        zelf verantwoordelijk voor je voedings- en gezondheidskeuzes. Raadpleeg een professional bij
        twijfel of bij medische aandoeningen.
      </p>
    </Section>

    <Section heading="7. Beschikbaarheid" testid="terms-section-availability">
      <p>
        We doen ons best Smartly betrouwbaar beschikbaar te houden, maar de dienst kan worden
        aangepast, tijdelijk niet bereikbaar zijn of bijgewerkt worden zonder vooraankondiging.
      </p>
    </Section>

    <Section heading="8. Aansprakelijkheid" testid="terms-section-liability">
      <p>
        Smartly wordt aangeboden "zoals het is". Voor zover wettelijk toegestaan zijn wij niet
        aansprakelijk voor indirecte schade of gemiste besparingen die voortvloeien uit het gebruik
        van de app. Niets in deze voorwaarden beperkt rechten die je dwingendrechtelijk hebt als
        consument.
      </p>
    </Section>

    <Section heading="9. Wijzigingen in deze voorwaarden" testid="terms-section-changes">
      <p>
        We kunnen deze voorwaarden van tijd tot tijd aanpassen. Belangrijke wijzigingen communiceren
        we via de app of per e-mail. Doorgaan met gebruik na een wijziging betekent acceptatie van de
        nieuwe voorwaarden.
      </p>
    </Section>

    <Section heading="10. Contact" testid="terms-section-contact">
      <p>Vragen over deze voorwaarden? Neem contact op met {OPERATOR} via {CONTACT_EMAIL}.</p>
      <ContactBlock testid="terms-contact-block" />
    </Section>
  </LegalShell>
);


// --------------------------------------------------------- Refund / cancel

export const RefundPolicyPage = () => (
  <LegalShell title="Opzeggen en restitutie" kicker="Opzeggen &amp; restitutie" testid="refund-policy-page">
    <Section heading="1. Gratis gebruik" testid="refund-section-free">
      <p>
        Smartly Free kun je zonder betaling gebruiken binnen de gepubliceerde limieten. Er is hierbij
        niets om op te zeggen of terug te storten.
      </p>
    </Section>

    <Section heading="2. Premium opzeggen" testid="refund-section-cancel">
      <p>
        Een Premium-abonnement kun je op elk moment opzeggen vanuit je Smartly-profiel. De afhandeling
        loopt via het Stripe-klantportaal.
      </p>
    </Section>

    <Section heading="3. Maandabonnement" testid="refund-section-monthly">
      <p>
        Bij een maandelijks Premium-abonnement blijft je toegang doorgaans actief tot het einde van de
        lopende betaalperiode. Er volgt geen automatische incasso voor de volgende periode na
        opzegging.
      </p>
    </Section>

    <Section heading="4. Jaarabonnement" testid="refund-section-annual">
      <p>
        Bij een jaarlijks Premium-abonnement blijft je toegang doorgaans actief tot het einde van de
        lopende betaalperiode. De jaarvergoeding wordt in beginsel niet pro rata terugbetaald na
        opzegging.
      </p>
    </Section>

    <Section heading="5. Restituties" testid="refund-section-refunds">
      <p>
        Restituties worden niet automatisch verleend, behalve waar de wet dat verplicht (bijvoorbeeld
        bij wettelijk herroepingsrecht voor digitale diensten waar dat van toepassing is). Twijfel je
        of je in aanmerking komt? Neem contact op voordat je opzegt.
      </p>
    </Section>

    <Section heading="6. Hulp nodig" testid="refund-section-support">
      <p>Voor vragen over je abonnement of factuur kun je contact opnemen met {OPERATOR} via {CONTACT_EMAIL}.</p>
      <ContactBlock testid="refund-contact-block" />
    </Section>

    <Section heading="7. Beheer via Stripe" testid="refund-section-stripe">
      <p>
        Zodra Premium actief is, verloopt het beheer van betaalmethode, facturen en opzegging via het
        Stripe-klantportaal. Je vindt de knop "Beheer abonnement" in je Smartly-profiel.
      </p>
    </Section>
  </LegalShell>
);
